
import React from 'react';
import { DaySimulation } from '../types';
import { Sun, Battery, BatteryMedium, BatteryWarning, Clock } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { BaseModal } from './BaseModal';

interface Props {
  simulation: DaySimulation;
  onClose: () => void;
}

export const DayInLifeSimulator: React.FC<Props> = ({ simulation, onClose }) => {
  const { theme } = useTheme();
  const fStyle = theme.colors.features.simulator;

  return (
    <BaseModal 
        onClose={onClose} 
        title={simulation.careerTitle}
        icon={<><Sun size={14} /> Day In The Life Simulator</>}
        headerColor={fStyle}
        maxWidth="max-w-lg"
        footer={
            <div className={`text-center`}>
                <button onClick={onClose} className={`text-sm font-medium ${theme.colors.text.secondary} hover:${theme.colors.text.main}`}>
                    Close Simulation
                </button>
            </div>
        }
    >
        <div className={`p-6 space-y-6 relative before:absolute before:left-10 before:top-8 before:bottom-8 before:w-0.5 ${theme.mode === 'dark' ? 'before:bg-slate-700' : 'before:bg-gray-200'}`}>
            {simulation.schedule.map((item, idx) => (
              <div key={idx} className="relative pl-10 group">
                {/* Timeline Dot */}
                <div className={`absolute left-[35px] top-1.5 w-3 h-3 rounded-full border-2 border-white shadow-sm z-10 ${
                    item.mood === 'energized' ? 'bg-green-500' :
                    item.mood === 'drained' ? 'bg-red-500' : 'bg-gray-400'
                }`}></div>

                {/* Card */}
                <div className={`rounded-xl p-4 transition-all border border-transparent hover:shadow-md ${theme.mode === 'dark' ? 'bg-slate-800 hover:border-slate-600' : 'bg-gray-50 hover:bg-white hover:border-gray-100'}`}>
                  <div className="flex justify-between items-start mb-1">
                    <span className={`text-xs font-bold flex items-center gap-1 ${theme.colors.text.secondary}`}>
                        <Clock size={12} /> {item.time}
                    </span>
                    {item.mood === 'energized' && <Battery size={16} className="text-green-500" />}
                    {item.mood === 'neutral' && <BatteryMedium size={16} className="text-gray-400" />}
                    {item.mood === 'drained' && <BatteryWarning size={16} className="text-red-500" />}
                  </div>
                  <h4 className={`font-bold ${theme.colors.text.main}`}>{item.activity}</h4>
                  <p className={`text-sm mt-1 leading-relaxed ${theme.colors.text.secondary}`}>{item.description}</p>
                </div>
              </div>
            ))}
        </div>

        <div className={`mx-6 mb-6 p-4 rounded-xl border text-sm font-medium italic text-center ${theme.mode === 'dark' ? 'bg-indigo-900/20 border-indigo-800 text-indigo-200' : 'bg-indigo-50 border-indigo-100 text-indigo-900'}`}>
             "{simulation.summary}"
        </div>
    </BaseModal>
  );
};
